import React, { useState, useEffect, useRef, createRef } from "react";
import { useLocation } from 'react-router-dom';
import { withStyles } from "@material-ui/core/styles";
import {
  Typography,
  Tooltip,
  SvgIcon,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  IconButton,
  Collapse,
	CircularProgress,
  Switch
} from "@material-ui/core";
import { DialogWithFocusProvider} from 'components/layout';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import RefreshIcon from '@material-ui/icons/Refresh';
import DeleteIcon from '@material-ui/icons/Delete';
import SaveIcon from '@material-ui/icons/Save';
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import StarIcon from "@material-ui/icons/Star";
import StarOutlinedIcon from "@material-ui/icons/StarBorder";
import { fetchChannelWorkflowGridLayouts, setChannelWorkflowGridSelectedLayoutId, saveChannelWorkflowGridLayout, updateChannelWorkflowGridLayout,
	deleteChannelWorkflowGridLayout, favoriteChannelWorkflowGridLayout, setConfirmationModalState, appNotify, 
  setPostAcqWorkflowGridSelectedLayoutId } from "store/actions";
import { channelWorkflowGridOldestLayoutIdSelector, postAcqWorkflowGridOldestLayoutIdSelector } from 'store/selectors';
import { connect } from "react-redux";
import { compose } from "recompose";
import { Scrolling, Column, HeaderFilter, SearchPanel } from "devextreme-react/data-grid";
import { Stack, LoadingOverlayWrapper } from "components/layout";
import { sendClientLog } from 'services/logService';
import DataGrid from 'components/devExtreme/DataGrid';
import { Constants } from 'utilities/constants';
import classNames from 'classnames';

const nameMaxLength = 100;
const layoutGridRef = createRef();

function LayoutPopover({ classes, gridRef, user, channelWorkflowGridLayouts, channelWorkflowGridOldestLayoutId,
	channelWorkflowLoadingOverlayState, channelWorkflowGridLayoutIsDiverted, channelWorkflowGridSelectedLayoutId,
  fetchChannelWorkflowGridLayouts, setChannelWorkflowGridSelectedLayoutId, saveChannelWorkflowGridLayout,
  updateChannelWorkflowGridLayout, deleteChannelWorkflowGridLayout,favoriteChannelWorkflowGridLayout,
  setConfirmationModalState, postAcqWorkflowGridSelectedLayoutId, setPostAcqWorkflowGridSelectedLayoutId, 
  postAcqWorkflowGridOldestLayoutId, postAcqWorkflowGridLayoutIsDiverted, postAcqWorkflowGridLayouts, hasChannelWorkflowGridState, hasPostAcqWorkflowGridState
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [layoutNameInput, setLayoutNameInput] = useState('');
  const [layoutNameError, setLayoutNameError] = useState('');
  const [viewType, setViewType] = useState('Private');
  const [saveLayoutExpanded, setSaveLayoutExpanded] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
  const [favoritesLocalState, setFavoritesLocalState] = useState(null);
  const [isFavoritesOnlyFilterApplied, setIsFavoritesOnlyFilterApplied] = useState(false);
  const [isFavoritesRequestPending, setIsFavoritesRequestPending] = useState(false);
  const location = useLocation();
  const isPostAcquisitionLayout = location.pathname === '/postAcq/workflow';
  let layoutType = 'Acquisition'; 
  let gridLayouts = channelWorkflowGridLayouts; 
  let selectedLayoutId = channelWorkflowGridSelectedLayoutId;
  let oldestLayoutId = channelWorkflowGridOldestLayoutId;
  let layoutConditionalText = 'Channel Workflow';
  let hasWorkflowGridState = hasChannelWorkflowGridState;

  if (isPostAcquisitionLayout) {
    layoutType = 'PostAcquisition'; 
    gridLayouts = postAcqWorkflowGridLayouts; 
    selectedLayoutId = postAcqWorkflowGridSelectedLayoutId;
    oldestLayoutId = postAcqWorkflowGridOldestLayoutId;
    layoutConditionalText = 'Post-Acq';
    hasWorkflowGridState = hasPostAcqWorkflowGridState;
  }

  const setGridSelectedLayoutId = (id, layoutType) => {
    return isPostAcquisitionLayout ?  setPostAcqWorkflowGridSelectedLayoutId(id, layoutType,) :  setChannelWorkflowGridSelectedLayoutId(id, layoutType);
  };

  useEffect(() => {
    fetchChannelWorkflowGridLayouts(layoutType);
  }, []);

  const isFirst = useRef(true);
  useEffect(() => {
    if (!gridLayouts?.length || !isFirst.current || (gridLayouts && gridLayouts[0].layoutType !== layoutType)) return;
    isFirst.current = false; // Only run on first fetch of layouts, otherwise refreshes will also trigger setting and fetching layout by id
    let id = selectedLayoutId != null && gridLayouts.find(layout => layout.id === selectedLayoutId)
      ? selectedLayoutId
      : oldestLayoutId; // Use user's last saved id if it exists in active layouts, else use oldest layout, set as selected id to trigger fetch in channelWorkflowGridLayoutSaga
    // check if id exists in layouts as control may be switching from Acq grid to post acq grid, if id belongs to different grid i.e. id from workflow but layouts are for post acq then it will throw 400 bad request
    const isIdExistsInChannelWorkflowGridLayouts = gridLayouts.find(layout => layout.id === id);
    if(!isIdExistsInChannelWorkflowGridLayouts) {
      id = gridLayouts[0].id; // layouts are sorted by id asc. i.e. oldest id
    }
    setGridSelectedLayoutId(id, layoutType);
  }, [gridLayouts, selectedLayoutId, oldestLayoutId, layoutType]);

  useEffect(() => {
    if (anchorEl || isFavoritesOnlyFilterApplied) { // Refresh when viewing only favorites, to pull latest favorited on data
      fetchChannelWorkflowGridLayouts(layoutType, false);
    }
  }, [anchorEl, isFavoritesOnlyFilterApplied]);

  useEffect(() => {
    if (!gridLayouts) return;
    const favoritesObj = {};
    gridLayouts.forEach(layout => favoritesObj[layout.id] = {
      isFavorite: layout.id === oldestLayoutId ? true : layout.isFavorite,
      favoritesCount: layout.favoritesCount
    });
    setFavoritesLocalState(favoritesObj);
  }, [channelWorkflowGridLayouts, channelWorkflowGridOldestLayoutId, postAcqWorkflowGridOldestLayoutId, postAcqWorkflowGridLayouts]);

  const togglePopover = (e) => {
    const el = anchorEl ? null : e.currentTarget;
    setAnchorEl(el);
  }

  const handleRefreshLayouts = () => {
    fetchChannelWorkflowGridLayouts(layoutType);
    sendClientLog('Info', `${layoutConditionalText} - Layouts: User clicked workflow layout refresh`);
  }

	const handleLayoutNameInputChange = e => {
    if (e.target.value?.length > nameMaxLength) {
      setLayoutNameError(`Name cannot exceed ${nameMaxLength} characters.`);
    } else if (layoutNameError?.length) {
      setLayoutNameError('');
    }
    setLayoutNameInput(e.target.value);
  }

  const handleViewTypeChange = e => {
    setViewType(e.target.value);
  }

	const closeConfirmationModal = () => {
    setConfirmationModalState({ isModalOpen: false });
  }

  const applyColumnRender = (e) => {
    return (
			<Stack horizontal>
				<Tooltip title={"Apply"} placement="bottom">
					<SvgIcon
						onClick={() => {
							handleApplySavedLayout(e);
						}}
						className={classes.btnApply}
						disabled={channelWorkflowLoadingOverlayState.isLoading}
					>
						<path d="M19.78,2.2L24,6.42L8.44,22L0,13.55L4.22,9.33L8.44,13.55L19.78,2.2M19.78,5L8.44,16.36L4.22,12.19L2.81,13.55L8.44,19.17L21.19,6.42L19.78,5Z" />
					</SvgIcon>
				</Tooltip>
			</Stack>
    );
  };

  const commandColumnRender = e => {
    const { id, canDelete } = e.data;
    if (!favoritesLocalState?.[id]) return null;
    const { isFavorite } = favoritesLocalState[id];
    const isOldest = id === oldestLayoutId;
    return (
			<Stack horizontal>
        <Tooltip title={`${isFavorite ? 'Remove' : 'Add'} Favorite`} placement="bottom">
					<span style={{ marginRight: 7 }}>
						<IconButton
							style={{
								padding: 2,
								margin: -2
							}}
							onClick={() => handleFavoriteLayout(e, !isFavorite)}
							disabled={channelWorkflowLoadingOverlayState.isLoading || isOldest || isFavoritesRequestPending}
						>
							{isFavorite ? <StarIcon /> : <StarOutlinedIcon />}
						</IconButton>
					</span>
				</Tooltip>
        {canDelete &&
          <>
            <Tooltip title={"Update"} placement="bottom">
              <span style={{ marginRight: 7 }}>
                <IconButton
                  style={{
                    padding: 2,
                    margin: -2
                  }}
                  onClick={() => handleUpdateLayout(e)}
                  disabled={(isOldest && !user.roles.administrator) || channelWorkflowLoadingOverlayState.isLoading}
                >
                  <SaveIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={"Delete"} placement="bottom">
              <span>
                <IconButton
                  style={{
                    padding: 2,
                    margin: -2
                  }}
                  onClick={() => handleDeleteLayout(e)}
                  disabled={isOldest || channelWorkflowLoadingOverlayState.isLoading}
                >
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          </>
        }
			</Stack>
  	)
  }

	const handleApplySavedLayout = (e) => {
		const { id } = e.row.data;
    setGridSelectedLayoutId(id, layoutType);
    togglePopover(e);
  };

  const handleFavoriteLayout = (e, requestedState) => {
    setIsFavoritesRequestPending(true);
    const { id } = e.row.data;
    const prevCount = favoritesLocalState[id].favoritesCount;
    const onSuccess = () => {
      setFavoritesLocalState({...favoritesLocalState, [id]: { isFavorite: requestedState, favoritesCount: prevCount + (requestedState ? 1 : -1) }});
      setIsFavoritesRequestPending(false);
    }
    const onError = () => {
      setIsFavoritesRequestPending(false);
    }

    favoriteChannelWorkflowGridLayout(id, requestedState, layoutType, onSuccess, onError);
  };

	const handleUpdateLayout = (e) => {
		const state = gridRef.current.instance.state()
		console.log(state)
		const { id, name, viewType } = e.row.data;
		const req = {
			id,
			name,
			viewType,
			jsonValue: state
		}
		setConfirmationModalState({
			isModalOpen: true,
      handleConfirmation: () => {
				closeConfirmationModal();
				updateChannelWorkflowGridLayout(req, layoutType);
			},
      handleCancellation: () => {
				closeConfirmationModal();
			},
      titleText: 'Confirm',
      contentText: `Are you sure you want to update this layout with the current ${layoutConditionalText.toLowerCase()} grid state?`
		})
  };

  const handleDeleteLayout = e => {
    const { id } = e.row.data;
		setConfirmationModalState({
			isModalOpen: true,
      handleConfirmation: () => {
				closeConfirmationModal();
				deleteChannelWorkflowGridLayout(id, layoutType, onSuccess);
			},
      handleCancellation: () => {
				closeConfirmationModal();
			},
      titleText: 'Confirm',
      contentText: `Are you sure you want to delete this layout?`
		})
		const onSuccess = () => {
		}
  }

  const handleSaveNewLayout = () => {
    if (isSaving) return;
    if (layoutNameInput === '') {
      setLayoutNameError('Layout name is required');
      return;
    }
		setIsSaving(true);
		const state = gridRef.current.instance.state()
		console.log(state)
    const req = {
      name: layoutNameInput,
      viewType,
      jsonValue: state
    }
		const callback = (success, error) => {
			if (success) {
				setLayoutNameInput('');
			} else {
				console.log(error);
				if (error.message) {
          if (error.message.includes('already exists')) {
						console.log('name already exists')
						setLayoutNameError('A layout with this name already exists');
					}
				}
			}
			setIsSaving(false);
		}
		saveChannelWorkflowGridLayout(req, layoutType, callback);
  }

  const getLayouts = (channelWorkflowGridLayouts) => {
    if (isFavoritesOnlyFilterApplied) {
      return channelWorkflowGridLayouts.filter(layout => favoritesLocalState[layout.id]?.isFavorite);
    }
    return channelWorkflowGridLayouts;
  }

  const toggleFavoritesOnly = () => {
    const newIsFavoritesOnly = !isFavoritesOnlyFilterApplied;
    setIsFavoritesOnlyFilterApplied(newIsFavoritesOnly);
    if (newIsFavoritesOnly) {
      layoutGridRef.current.instance.beginUpdate();
      layoutGridRef.current.instance.clearSorting();
      layoutGridRef.current.instance.columnOption('favoritedOn', { visible: true, sortOrder: 'desc'});
      layoutGridRef.current.instance.endUpdate();
    } else {
      layoutGridRef.current.instance.beginUpdate();
      layoutGridRef.current.instance.clearSorting();
      layoutGridRef.current.instance.columnOption('favoritedOn', { visible: false });
      layoutGridRef.current.instance.columnOption('createdOn', { sortOrder: 'desc' });
      layoutGridRef.current.instance.endUpdate();
    }
    sendClientLog('Info', `${layoutConditionalText}- Layouts: User toggled My Favorites Only to ${newIsFavoritesOnly}`);
  }

  const isLayoutModified = () => {
    let title = '';
    if (isPostAcquisitionLayout && postAcqWorkflowGridLayoutIsDiverted) {
      title = 'The selected Post-Acq layout has been modified';
    } else if(channelWorkflowGridLayoutIsDiverted) {
      title = 'The selected layout has been modified';
    }
    return title;
  };

  if (!gridLayouts) return null;
  const foundLayout = gridLayouts.find(el => el.id === selectedLayoutId);
  const layoutName = !gridLayouts.length || !foundLayout
    ? 'None'
    : foundLayout.name;
  return (
    <>
      <Tooltip title={isLayoutModified()}>
        <Button
          className={classNames(classes.layoutButton, ((isPostAcquisitionLayout && postAcqWorkflowGridLayoutIsDiverted) || channelWorkflowGridLayoutIsDiverted) && classes.divertedButton)}
          color={'primary'}
          variant={'contained'}
          onClick={togglePopover}
          disabled={channelWorkflowLoadingOverlayState.isLoading}
        >
          <span className={classes.ellipsis} title={layoutName}>
            {`Layout: ${layoutName}`}
          </span>
          {((isPostAcquisitionLayout && postAcqWorkflowGridLayoutIsDiverted) || channelWorkflowGridLayoutIsDiverted) &&
            <span style={{ position: 'absolute', top: 3, right: 36 }}>*</span>
          }
          <ArrowDropDownIcon className={classes.buttonRightIcon} style={((isPostAcquisitionLayout && postAcqWorkflowGridLayoutIsDiverted) || channelWorkflowGridLayoutIsDiverted) ? {marginLeft: 8} : {}} />
        </Button>
      </Tooltip>

      <DialogWithFocusProvider
        variant='popover'
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={togglePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <div className={classes.container}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: -44 }}>
            <Typography variant="subtitle1" style={{ zIndex: 1, paddingTop: 4 }}>Saved Layouts</Typography>
            <IconButton
              className={classes.refreshButton}
              onClick={handleRefreshLayouts}
              style={{zIndex: 1}}
              disabled={channelWorkflowLoadingOverlayState.isLoading}
            >
              <RefreshIcon />
            </IconButton>
            <div className={classes.flexGrow}></div>
            <Tooltip title='Toggle Only My Favorite Layouts' placement='bottom'>
              <FormControlLabel
                control={<Switch checked={isFavoritesOnlyFilterApplied} onChange={toggleFavoritesOnly} />}
                label="My Favorites Only"
                className={classes.favoritesOnlyFilterSwitch}
              />
            </Tooltip>
          </div>
          <LoadingOverlayWrapper
            active={channelWorkflowLoadingOverlayState.isLoading}
            message={channelWorkflowLoadingOverlayState.message}
          >
            <DataGrid
              ref={layoutGridRef}
              id='savedFilterGrid'
              keyExpr={'id'}
              persistState
              dataSource={getLayouts(gridLayouts).map(layout => {
                return {
                  ...layout,
                  favoritesCount: favoritesLocalState?.[layout.id]?.favoritesCount,
                  canDelete: user.roles.administrator ? true : layout.userId === user.id
                }
              })}
              height={466}
              paging={{ enabled: false }}
              hoverStateEnabled
              selectedRowKeys={[selectedLayoutId]}
            >
              <Column width={48} cellRender={applyColumnRender} />
              <Column dataField={"name"} />
              <Column dataField={"createdOn"} dataType={"date"} sortOrder={"desc"} format={"MM/dd/yyyy"} width={120} />
              <Column dataField={"modifiedOn"} dataType={"date"} format={"MM/dd/yyyy"} width={120} />
              <Column dataField={"createdBy"} width={140} />
              <Column dataField={"updatedBy"} width={140} />
              <Column dataField={"viewType"} width={100} allowFiltering={true} />
              <Column dataField={"favoritesCount"} caption={'Favorites'} width={80} allowFiltering={false} alignment='left' />
              <Column dataField={"favoritedOn"} dataType={"date"} format={"MM/dd/yyyy"} width={120} visible={false} />
              <Column cellRender={commandColumnRender} alignment='center' width={106} />
              <Scrolling
                rowRenderingMode='virtual'
              />
              <HeaderFilter
                visible={true}
                allowSearch={true}
                hideSelectAllOnSearch={false}
              />
              <SearchPanel
                visible={true}
                searchVisibleColumnsOnly={true}
              />
            </DataGrid>
          </LoadingOverlayWrapper>
          <div className={classes.spacer} />
          <div style={{ display: 'flex' }}>
            <Typography variant="subtitle1">Save New Layout</Typography>
            <IconButton
              className={classes.expandButton}
              style={{transform: `rotate(${saveLayoutExpanded ? '180' : '0'}deg)`,}}
              onClick={() => setSaveLayoutExpanded(!saveLayoutExpanded)}
            >
              <ExpandMoreIcon />
            </IconButton>
          </div>
          <Collapse in={saveLayoutExpanded}>
            <div style={{display: 'flex', alignItems: 'center', border: '1px solid #e0e0e0', padding: '8px 8px 4px'}}>
              <TextField
                label='Layout Name'
                placeholder='Enter a name...'
                onChange={handleLayoutNameInputChange}
                onBlur={() => setLayoutNameInput(layoutNameInput.trim())}
                value={layoutNameInput}
                style={{width: 308, height: 68, marginRight: 16}}
                InputLabelProps={{
                  shrink: true,
                }}
                error={layoutNameError?.length > 0}
                helperText={layoutNameError}
              />
              <FormControl component="fieldset" style={{marginRight: 10, marginBottom: 12}}>
                <FormLabel component="legend">View Type</FormLabel>
                <RadioGroup row aria-label="viewType" name="viewType" value={viewType} onChange={handleViewTypeChange} style={{flexWrap: 'nowrap'}}>
                  <FormControlLabel value="Private" control={<Radio size='small'/>} label="Private" />
                  <FormControlLabel value="Public" control={<Radio size='small'/>} disabled={!user.roles.layoutManager && !user.roles.administrator} label="Public" />
                </RadioGroup>
              </FormControl>
              <div style={{flexGrow: 1, textAlign: 'end'}}>
                <Typography variant='caption'>
                  {`The ${layoutConditionalText} grid's current state will be saved as a layout.`}
                </Typography>
              </div>
              <Button
                variant='outlined'
                color='primary'
                onClick={handleSaveNewLayout}
                disabled={layoutNameError?.length > 0 || channelWorkflowLoadingOverlayState.isLoading}
                style={{margin: '0 8px', width: 66}}
              >
                {isSaving ? <CircularProgress size={24} /> : 'Save'}
              </Button>
            </div>
          </Collapse>
        </div>
      </DialogWithFocusProvider>
    </>
  );
}

const styles = (theme) => ({
  container: {
    padding: 8,
    width: 1200
  },
  layoutButton: {
    margin: theme.spacing(1),
    color: 'white',
    marginLeft: 16,
    maxWidth: 350,
    position: 'relative'
  },
  divertedButton: {
    backgroundColor: '#808f9f',
    '&:hover': {
      backgroundColor: '#6e7a87'
    }
  },
  buttonRightIcon: {
    marginLeft: 4,
    fontSize: 20,
  },
  warningIcon: {
    fill: theme.palette.warning.text
  },
  spacer: {
    height: 16,
  },
  btnApply: {
    padding: 2,
    background: "rgba(0, 0, 0, 0.1)",
    borderRadius: `5px`,
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.3)",
      cursor: `pointer`,
    },
  },
  refreshButton: {
    padding: 2,
    marginLeft: 6
  },
  expandButton: {
    transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    padding: 2,
    marginLeft: 6
  },
  ellipsis: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis'
  },
  flexGrow: {
    flexGrow: 1
  },
  favoritesOnlyFilterSwitch: {
    zIndex: 1,
    marginLeft: 8,
    marginRight: 200
  }
});

function mapState(state) {
  const { channelWorkflowState, postAcqWorkflowState } = state;
  return {
		user: state.appState.user,
    channelWorkflowGridLayouts: channelWorkflowState.channelWorkflowGridLayouts,
		channelWorkflowLoadingOverlayState: channelWorkflowState.channelWorkflowLoadingOverlayState,
    channelWorkflowGridSelectedLayoutId: channelWorkflowState.channelWorkflowGridSelectedLayoutId,
    channelWorkflowGridOldestLayoutId: channelWorkflowGridOldestLayoutIdSelector(state),
    channelWorkflowGridLayoutIsDiverted: channelWorkflowState.channelWorkflowGridLayoutIsDiverted,
    postAcqWorkflowGridSelectedLayoutId: postAcqWorkflowState.postAcqWorkflowGridSelectedLayoutId,
    postAcqWorkflowGridOldestLayoutId: postAcqWorkflowGridOldestLayoutIdSelector(state),
    postAcqWorkflowGridLayoutIsDiverted: postAcqWorkflowState.postAcqWorkflowGridLayoutIsDiverted,
    postAcqWorkflowGridLayouts: postAcqWorkflowState.postAcqWorkflowGridLayouts,
    hasChannelWorkflowGridState: channelWorkflowState.channelWorkflowGridState,
    hasPostAcqWorkflowGridState: postAcqWorkflowState.postAcqWorkflowGridState
  };
}

const mapDispatch = {
	appNotify,
  fetchChannelWorkflowGridLayouts,
	setChannelWorkflowGridSelectedLayoutId,
	saveChannelWorkflowGridLayout,
	updateChannelWorkflowGridLayout,
	deleteChannelWorkflowGridLayout,
  favoriteChannelWorkflowGridLayout,
	setConfirmationModalState,
  setPostAcqWorkflowGridSelectedLayoutId
};

export default compose(
	withStyles(styles),
	connect(
		mapState,
		mapDispatch
	)
)(LayoutPopover);
